// Scan-side "Request Scan" page.
//
// Mirror of RequestLab.js for the imaging/scan workflow. A scan supervisor can
// initiate a scan request for a patient without routing through a clinician,
// using the same POST /requestLab payload with type: 'Scan' so the backend's
// billing logic kicks in:
//
//   • amount  > 0  → lab_charge row with status='pending' and investegation='Scan'.
//                    The request sits in Accounts until payment is processed;
//                    only then is a corresponding lab_request row created, and
//                    the patient surfaces in Scan Dashboard / Requested Scan.
//   • amount == 0  → request is treated as free / fully insured: both lab_charge
//                    and lab_request rows are written immediately, so the
//                    patient appears in the scan worklist on the next refresh.
//
// We never bypass that flow — the scan supervisor is just a different
// originator of an otherwise identical request.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Button, IconButton,
  Avatar, Chip, Divider, Stack, Grid, Card, CardActionArea, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, CircularProgress, Snackbar, Alert,
  Tooltip, useTheme, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Scanner as ScannerIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  LocalHospital as HospitalIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as DoctorIcon,
  DirectionsWalk as WalkInIcon,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';

import {
  loadAllPatientsToday, loadAllInPatients, loadAvailableScansdb, requestLab,
  loadRequestedScanInpatient, onboardedDoctorsListUrl,
} from '../BaseURL';
import { formatWithOutSom } from '../Message';
import { computeAge } from '../../utils/age';

const FILTERS = [
  { value: 'outpatient', label: 'Outpatients' },
  { value: 'inpatient',  label: 'Inpatients'  },
];

export default function RequestScan() {
  // ── Theme tokens — violet accent so the scan side reads as distinct from
  // the teal lab side, while keeping the same surface palette.
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = useMemo(() => ({
    isDark,
    pageBg:       isDark ? '#000000' : '#F4F6FA',
    surface:      isDark ? '#0E0E14' : '#FFFFFF',
    surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
    fieldBg:      isDark ? '#14141C' : '#FFFFFF',
    borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
    text:         isDark ? '#FFFFFF' : '#0F172A',
    textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
    textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
    rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
    accent: {
      teal:    '#11998e',
      green:   '#22c55e',
      emerald: '#10b981',
      blue:    '#3b82f6',
      amber:   '#f59e0b',
      danger:  '#ef4444',
      violet:  '#8b5cf6',
      indigo:  '#6366f1',
    },
  }), [isDark]);

  const today = moment().format('YYYY-MM-DD');

  // ── Patient list state ────────────────────────────────────────────────
  const [filter, setFilter]               = useState('outpatient');
  const [patients, setPatients]           = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  // ── Selected patient + scan-request workspace ─────────────────────────
  // Requests always record against today — no back-dating or forward-dating.
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [scans, setScans]               = useState([]);
  const [scansLoading, setScansLoading] = useState(false);
  const [scanSearch, setScanSearch]     = useState('');
  const [requestingScanId, setRequestingScanId] = useState(null);

  const [existingRequests, setExistingRequests] = useState([]); // server-side: today's requests
  const [sessionRequests, setSessionRequests]   = useState([]); // local: requested this visit

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentUser, setCurrentUser] = useState('');

  // Referring / requesting clinician — see RequestLab.js / RequestServices.js
  // for the full rationale. Same shape so doc_name attribution is
  // consistent across all three request entry points.
  const WALK_IN = useMemo(() => ({ id: '__walkin__', name: 'Walk-in', _walkin: true }), []);
  const [doctorOptions, setDoctorOptions] = useState([WALK_IN]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [referringDoctor, setReferringDoctor] = useState(null);

  // ── Pull logged-in username so the backend can attribute the request to
  //    the scan supervisor (otherwise it logs as anonymous).
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        setCurrentUser(u?.name || u?.username || '');
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  // ── Onboarded doctors loader ─────────────────────────────────────────
  const loadDoctors = async () => {
    setDoctorsLoading(true);
    try {
      const res = await axios.get(onboardedDoctorsListUrl, { params: { status: 'active' }, timeout: 12000 });
      const rows = res.data?.success ? (res.data.results || []) : [];
      setDoctorOptions([WALK_IN, ...rows]);
    } catch (err) {
      console.error('Failed to load onboarded doctors:', err.message);
      setDoctorOptions([WALK_IN]);
    } finally {
      setDoctorsLoading(false);
    }
  };
  useEffect(() => { loadDoctors(); /* eslint-disable-next-line */ }, []);

  // ── Patient list loader ───────────────────────────────────────────────
  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const endpoint = filter === 'inpatient' ? loadAllInPatients : loadAllPatientsToday;
      const res = await axios.get(endpoint);
      if (res.data?.success) {
        setPatients(Array.isArray(res.data.results) ? res.data.results : []);
      } else {
        setPatients([]);
      }
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatients([]);
    } finally {
      setPatientsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedPatient) loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedPatient]);

  // ── Scan catalogue + already-requested loaders for the selected patient
  const loadScansForPatient = async (patient) => {
    setScansLoading(true);
    try {
      const res = await axios.get(loadAvailableScansdb, { params: { insurance: patient.insurance } });
      if (res.data?.success) setScans(res.data.results || []);
      else setScans([]);
    } catch (err) {
      console.error('Failed to load scan catalogue:', err);
      setScans([]);
    } finally {
      setScansLoading(false);
    }
  };

  const loadExistingRequests = async (patient) => {
    try {
      const res = await axios.get(loadRequestedScanInpatient, {
        params: { patientId: patient.id, dat: today, currentDate: today },
      });
      if (res.data?.success) setExistingRequests(res.data.results || []);
    } catch (err) {
      // Non-fatal: the duplicate check just won't fire.
      setExistingRequests([]);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setScanSearch('');
    setSessionRequests([]);
    loadScansForPatient(patient);
    loadExistingRequests(patient);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
    setScans([]);
    setExistingRequests([]);
    setSessionRequests([]);
    setScanSearch('');
  };

  // ── Request a single scan (same payload as the doctor flow, type='Scan')
  const handleRequestScan = async (scan, scanRowKey) => {
    if (!selectedPatient) return;

    // Referring doctor must be chosen first — same rule as RequestLab.
    if (!referringDoctor) {
      setSnackbar({
        open: true, severity: 'error',
        message: 'Select a referring doctor (or "Walk-in") before requesting.',
      });
      return;
    }

    // Duplicate guard — same scan, same day → ask before re-submitting so the
    // scan supervisor doesn't accidentally double-bill an insured patient.
    const dupServer = existingRequests.some(r => r.lab === scan.name && r.date === today);
    const dupSession = sessionRequests.some(r => r.scanName === scan.name);
    if (dupServer || dupSession) {
      const result = await Swal.fire({
        title: 'Already requested today',
        text: `${scan.name} has already been requested for ${selectedPatient.fullName} today. Request again?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, request again',
        cancelButtonText: 'Cancel',
        confirmButtonColor: t.accent.violet,
        cancelButtonColor: t.accent.danger,
      });
      if (!result.isConfirmed) return;
    }

    setRequestingScanId(scanRowKey);
    try {
      const res = await axios.post(requestLab, {
        patientId: selectedPatient.id,
        dat: today,
        patientInsurance: selectedPatient.insurance,
        labName: scan.name,
        amount: scan.amount,
        base_cost: scan.tariff,
        code: scan.code,
        currentUser,
        type: 'Scan',
        referringDoctor: referringDoctor.name,    // doc_name on every row
      });

      if (res.data?.success) {
        // Backend returns SCN-xxxxxx — show it on the toast and the
        // session strip so the supervisor reads the same string the
        // barcode slip will print.
        const reqNo = res.data?.reqNo || null;
        setSessionRequests(prev => [
          ...prev,
          {
            scanName: scan.name,
            amount: scan.amount,
            insured: Number(scan.amount) === 0,
            reqNo,
            at: new Date().toISOString(),
          },
        ]);
        setSnackbar({
          open: true,
          severity: 'success',
          message: `${reqNo ? reqNo + ' · ' : ''}${scan.name} ${
            Number(scan.amount) === 0
              ? 'requested — patient will appear on the Scan Dashboard shortly.'
              : 'sent to Accounts for payment.'
          }`,
        });
        // Refresh the server-side duplicate list so consecutive clicks see it.
        loadExistingRequests(selectedPatient);
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: res.data?.message || `Could not request ${scan.name}.`,
        });
      }
    } catch (err) {
      console.error('requestScan failed:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || `Could not request ${scan.name}.`,
      });
    } finally {
      setRequestingScanId(null);
    }
  };

  // ── Derived: filtered patient + scan lists ────────────────────────────
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(p =>
      String(p.id || '').toLowerCase().includes(q) ||
      String(p.fullName || '').toLowerCase().includes(q) ||
      String(p.tel || '').toLowerCase().includes(q)
    );
  }, [patients, patientSearch]);

  const filteredScans = useMemo(() => {
    const q = scanSearch.trim().toLowerCase();
    if (!q) return scans;
    return scans.filter(s => String(s.name || '').toLowerCase().includes(q));
  }, [scans, scanSearch]);

  // ────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.pageBg, p: { xs: 1.5, md: 3 } }}>
      {/* ── Page header ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          borderRadius: 2,
          bgcolor: t.surface,
          border: `1px solid ${t.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar sx={{
          bgcolor: alpha(t.accent.violet, 0.16),
          color: t.accent.violet,
          width: 44, height: 44,
          border: `1px solid ${alpha(t.accent.violet, 0.32)}`,
        }}>
          <ScannerIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: t.accent.violet, lineHeight: 1,
          }}>
            Investigations
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 700,
            color: t.text, mt: 0.25,
          }}>
            Request Scan
            <Typography component="span" sx={{
              fontSize: '0.78rem', fontWeight: 500, color: t.textMuted, ml: 1.25,
            }}>
              Send a scan order to Accounts on behalf of a patient
            </Typography>
          </Typography>
        </Box>

        {selectedPatient && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{
              textTransform: 'none', fontWeight: 600,
              color: t.textMuted, borderColor: t.borderStrong,
              '&:hover': { bgcolor: t.rowHover, borderColor: t.borderStrong },
            }}
            variant="outlined"
            size="small"
          >
            Back to patient list
          </Button>
        )}
      </Paper>

      {!selectedPatient ? (
        <PatientPicker
          t={t}
          filter={filter}
          setFilter={setFilter}
          patientSearch={patientSearch}
          setPatientSearch={setPatientSearch}
          filteredPatients={filteredPatients}
          patientsLoading={patientsLoading}
          onRefresh={loadPatients}
          onSelect={handleSelectPatient}
        />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' } }}>
          <Box>
            <PatientHeader t={t} patient={selectedPatient} />

            {/* Referring-doctor selector — gates every scan request below.
                Same control + logic used on RequestLab and the Medical
                Records request workspace so doc_name attribution is
                consistent across all three flows. */}
            <ReferringDoctorBar
              t={t}
              accent={t.accent.violet}
              options={doctorOptions}
              loading={doctorsLoading}
              value={referringDoctor}
              onChange={setReferringDoctor}
              onRefresh={loadDoctors}
            />

            <ScanCatalogue
              t={t}
              scans={filteredScans}
              scanSearch={scanSearch}
              setScanSearch={setScanSearch}
              today={today}
              scansLoading={scansLoading}
              requestingScanId={requestingScanId}
              onRequest={handleRequestScan}
              sessionRequests={sessionRequests}
              existingRequests={existingRequests}
            />
          </Box>
          {/* Sticky, self-scrolling right rail — only this column scrolls
              once the session/on-file lists grow, so the scan catalogue
              and patient header stay anchored. */}
          <Box
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 16 },
              alignSelf: 'flex-start',
              maxHeight: { lg: 'calc(100vh - 32px)' },
              overflowY: { lg: 'auto' },
              pr: { lg: 0.5 },
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: t.borderStrong,
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: t.textMuted },
              scrollbarWidth: 'thin',
              scrollbarColor: `${t.borderStrong} transparent`,
            }}
          >
            <RequestSidebar
              t={t}
              sessionRequests={sessionRequests}
              existingRequests={existingRequests}
              currentUser={currentUser}
            />
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Patient picker — shown before a patient is selected
// ──────────────────────────────────────────────────────────────────────
function PatientPicker({
  t, filter, setFilter, patientSearch, setPatientSearch,
  filteredPatients, patientsLoading, onRefresh, onSelect,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          fullWidth
          size="small"
          value={patientSearch}
          onChange={(e) => setPatientSearch(e.target.value)}
          placeholder="Search by name, patient ID, or phone…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: patientSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setPatientSearch('')}>
                  <ClearIcon fontSize="small" sx={{ color: t.textMuted }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5, bgcolor: t.fieldBg,
              '& fieldset': { borderColor: t.borderSubtle },
            },
          }}
        />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
              color: t.textMuted, borderColor: t.borderSubtle, px: 1.75,
              '&.Mui-selected': {
                bgcolor: alpha(t.accent.violet, 0.16),
                color: t.accent.violet,
                borderColor: alpha(t.accent.violet, 0.4),
                '&:hover': { bgcolor: alpha(t.accent.violet, 0.22) },
              },
            },
          }}
        >
          {FILTERS.map(f => (
            <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Tooltip title="Refresh list">
          <IconButton
            onClick={onRefresh}
            size="small"
            sx={{ border: `1px solid ${t.borderSubtle}`, color: t.textMuted }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ borderColor: t.borderSubtle, mb: 1.5 }} />

      {patientsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: t.accent.violet }} />
        </Box>
      ) : filteredPatients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 48, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {patientSearch ? 'No patients match that search.' : 'No active patients to show.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.25}>
          {filteredPatients.map((p) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${t.borderSubtle}`,
                  bgcolor: t.surfaceAlt,
                  transition: 'border-color 120ms ease, transform 120ms ease',
                  '&:hover': {
                    borderColor: alpha(t.accent.violet, 0.5),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardActionArea onClick={() => onSelect(p)} sx={{ p: 1.25 }}>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Avatar sx={{
                        bgcolor: p.state === 'inPatient'
                          ? alpha(t.accent.blue, 0.18)
                          : alpha(t.accent.violet, 0.16),
                        color: p.state === 'inPatient' ? t.accent.blue : t.accent.violet,
                        width: 40, height: 40, fontWeight: 700,
                      }}>
                        {String(p.fullName || '?').trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, color: t.text, fontSize: '0.92rem',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {p.fullName || '—'}
                        </Typography>
                        <Typography sx={{ color: t.textMuted, fontSize: '0.72rem' }}>
                          {p.id} · {p.gender || '—'} · {computeAge(p.dob)?.label || (p.age ? `${p.age}y` : '—')}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={p.insurance || 'Cash'}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: alpha(t.accent.amber, 0.16),
                          color: t.accent.amber,
                        }}
                      />
                      <Chip
                        size="small"
                        label={p.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: p.state === 'inPatient'
                            ? alpha(t.accent.blue, 0.16)
                            : alpha(t.accent.violet, 0.16),
                          color: p.state === 'inPatient' ? t.accent.blue : t.accent.violet,
                        }}
                      />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Selected-patient header
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
//  Referring-doctor selector — onboarded doctors + a pinned "Walk-in".
//  See RequestLab.js / RequestServices.js for the full rationale. Same
//  shape so doc_name attribution stays consistent across the three
//  request entry points.
// ──────────────────────────────────────────────────────────────────────
function ReferringDoctorBar({ t, accent, options, loading, value, onChange, onRefresh }) {
  const isWalkIn = value && value._walkin;
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, md: 1.25 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${value ? t.borderSubtle : alpha(accent, 0.45)}`,
        display: 'flex', alignItems: 'center', gap: 1.25,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(accent, 0.16),
        color: accent,
        width: 36, height: 36,
        border: `1px solid ${alpha(accent, 0.32)}`,
      }}>
        {isWalkIn ? <WalkInIcon sx={{ fontSize: 20 }} /> : <DoctorIcon sx={{ fontSize: 20 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 800, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: t.textMuted, lineHeight: 1, mb: 0.5,
        }}>
          Requesting on behalf of *
        </Typography>
        <Autocomplete
          size="small"
          options={options}
          value={value}
          loading={loading}
          onChange={(_, v) => onChange(v)}
          getOptionLabel={(o) => (o?._walkin ? 'Walk-in' : (o?.name || ''))}
          isOptionEqualToValue={(o, v) => (o?.id || o?.name) === (v?.id || v?.name)}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id || option.name} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: option._walkin ? `1px dashed ${t.borderSubtle}` : 'none',
            }}>
              {option._walkin
                ? <WalkInIcon sx={{ fontSize: 18, color: accent }} />
                : <DoctorIcon  sx={{ fontSize: 18, color: t.textMuted }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: option._walkin ? 800 : 600, color: t.text, fontSize: '0.86rem' }}>
                  {option._walkin ? 'Walk-in (no referring doctor)' : `Dr. ${option.name}`}
                </Typography>
                {!option._walkin && (option.specialization || option.facility_name) && (
                  <Typography sx={{ fontSize: '0.72rem', color: t.textMuted }}>
                    {[option.specialization, option.facility_name].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search onboarded doctors or pick Walk-in…"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
                  '& fieldset': { borderColor: t.borderSubtle },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={14} sx={{ color: accent, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ '& .MuiAutocomplete-popupIndicator': { color: t.textMuted } }}
        />
      </Box>
      {value && (
        <Chip
          size="small"
          icon={isWalkIn ? <WalkInIcon sx={{ fontSize: 14 }} /> : <DoctorIcon sx={{ fontSize: 14 }} />}
          label={isWalkIn ? 'Walk-in' : `Dr. ${value.name}`}
          sx={{
            height: 26, fontWeight: 700, fontSize: '0.72rem',
            bgcolor: alpha(accent, 0.16), color: accent,
            border: `1px solid ${alpha(accent, 0.32)}`,
          }}
        />
      )}
      <Tooltip title="Refresh doctor list">
        <span>
          <IconButton size="small" onClick={onRefresh}
            sx={{ color: t.textMuted, border: `1px solid ${t.borderSubtle}` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

function PatientHeader({ t, patient }) {
  const ageInfo = computeAge(patient.dob);
  const ageLabel = ageInfo?.label || (patient.age ? `${patient.age}y` : '—');
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(t.accent.violet, 0.16),
        color: t.accent.violet,
        width: 48, height: 48, fontWeight: 700, fontSize: '1.1rem',
        border: `1px solid ${alpha(t.accent.violet, 0.32)}`,
      }}>
        {String(patient.fullName || '?').trim().charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: t.text, fontSize: '1.05rem' }}>
          {patient.fullName}
          <Typography component="span" sx={{ ml: 1, fontWeight: 500, color: t.textMuted, fontSize: '0.85rem' }}>
            · ID {patient.id}
          </Typography>
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={patient.gender || '—'}     sx={chipSx(t, t.accent.blue)} />
          <Chip size="small" label={ageLabel}                  sx={chipSx(t, t.accent.emerald)} />
          <Chip size="small" label={patient.insurance || 'Cash'} sx={chipSx(t, t.accent.amber)} />
          <Chip
            size="small"
            label={patient.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
            sx={chipSx(t, patient.state === 'inPatient' ? t.accent.blue : t.accent.violet)}
          />
        </Stack>
      </Box>
    </Paper>
  );
}

function chipSx(t, color) {
  return {
    height: 22, fontSize: '0.7rem', fontWeight: 700,
    bgcolor: alpha(color, 0.16), color,
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}

// ──────────────────────────────────────────────────────────────────────
//  Scan catalogue + per-row Request button
// ──────────────────────────────────────────────────────────────────────
function ScanCatalogue({
  t, scans, scanSearch, setScanSearch, today,
  scansLoading, requestingScanId, onRequest, sessionRequests, existingRequests,
}) {
  const isRequested = (scan) =>
    sessionRequests.some(r => r.scanName === scan.name) ||
    existingRequests.some(r => r.lab === scan.name);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: `1px solid ${t.borderSubtle}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={scanSearch}
            onChange={(e) => setScanSearch(e.target.value)}
            placeholder="Search scans…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5, bgcolor: t.fieldBg,
                '& fieldset': { borderColor: t.borderSubtle },
              },
            }}
          />
          <Chip
            icon={<CalendarIcon sx={{ fontSize: 16 }} />}
            label={moment(today).format('ddd, MMM D, YYYY')}
            sx={{
              height: 36, px: 1, borderRadius: 1.5, fontWeight: 700,
              bgcolor: alpha(t.accent.violet, 0.12),
              color: t.accent.violet,
              border: `1px solid ${alpha(t.accent.violet, 0.32)}`,
              '& .MuiChip-icon': { color: t.accent.violet },
            }}
          />
        </Stack>
      </Box>

      {scansLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={26} sx={{ color: t.accent.violet }} />
        </Box>
      ) : scans.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ScannerIcon sx={{ fontSize: 44, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {scanSearch ? 'No scans match that search.' : 'No scans configured for this patient\'s insurance.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Scan', 'Code', 'Amount', 'Status', ''].map((h, i) => (
                  <TableCell
                    key={h + i}
                    align={i >= 2 ? (i === 4 ? 'center' : 'right') : 'left'}
                    sx={{
                      bgcolor: t.surfaceAlt,
                      color: t.textMuted,
                      borderBottom: `1px solid ${t.borderSubtle}`,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.map((scan, idx) => {
                const key  = `${scan.code || 'S'}-${idx}`;
                const done = isRequested(scan);
                const busy = requestingScanId === key;
                return (
                  <TableRow
                    key={key}
                    sx={{
                      '&:hover': { bgcolor: t.rowHover },
                      ...(done && { bgcolor: alpha(t.accent.emerald, 0.08) }),
                    }}
                  >
                    <TableCell sx={{ color: t.text, fontWeight: 600, borderColor: t.borderSubtle, textTransform: 'uppercase' }}>
                      {scan.name}
                    </TableCell>
                    <TableCell sx={{ color: t.textMuted, fontFamily: 'monospace', fontSize: '0.78rem', borderColor: t.borderSubtle }}>
                      {scan.code || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: t.text, fontWeight: 700, borderColor: t.borderSubtle }}>
                      {Number(scan.amount) === 0
                        ? <Chip size="small" label="Free" sx={chipSx(t, t.accent.emerald)} />
                        : formatWithOutSom(scan.amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: t.borderSubtle }}>
                      {done ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Requested"
                          sx={chipSx(t, t.accent.emerald)}
                        />
                      ) : Number(scan.amount) === 0 ? (
                        <Chip size="small" label="Insured" sx={chipSx(t, t.accent.blue)} />
                      ) : (
                        <Chip
                          icon={<HourglassIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Pay at Accounts"
                          sx={chipSx(t, t.accent.amber)}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: t.borderSubtle }}>
                      {busy ? (
                        <CircularProgress size={20} sx={{ color: t.accent.violet }} />
                      ) : (
                        <Tooltip title={done ? 'Request again' : 'Request'}>
                          <IconButton
                            size="small"
                            onClick={() => onRequest(scan, key)}
                            sx={{
                              bgcolor: alpha(t.accent.violet, 0.12),
                              color: t.accent.violet,
                              '&:hover': { bgcolor: alpha(t.accent.violet, 0.22) },
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Right-rail summary — session + existing requests + payment note
// ──────────────────────────────────────────────────────────────────────
function RequestSidebar({ t, sessionRequests, existingRequests, currentUser }) {
  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <InfoIcon sx={{ color: t.accent.violet, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            How this works
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.78rem', color: t.textMuted, lineHeight: 1.55 }}>
          Paid scans are routed to <strong style={{ color: t.text }}>Accounts</strong> for settlement
          first — the patient only appears on the <strong style={{ color: t.text }}>Scan Dashboard</strong>{' '}
          after payment is processed. Insured/free scans skip that step and are added to the worklist
          immediately.
        </Typography>
        {currentUser && (
          <Typography sx={{ fontSize: '0.72rem', color: t.textDim, mt: 1.25 }}>
            Requesting as <strong style={{ color: t.textMuted }}>{currentUser}</strong>
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Requested this session
          </Typography>
          <Chip
            size="small"
            label={sessionRequests.length}
            sx={chipSx(t, t.accent.violet)}
          />
        </Stack>
        {sessionRequests.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: t.textDim }}>
            Nothing requested yet. Click <SendIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> on a scan to start.
          </Typography>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: t.borderSubtle }} />} spacing={0.75}>
            {sessionRequests.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.82rem', color: t.text, fontWeight: 600,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {r.scanName}
                  </Typography>
                  {r.reqNo && (
                    <Typography sx={{
                      fontSize: '0.7rem', fontFamily: 'monospace',
                      color: t.accent.violet, fontWeight: 700, mt: 0.25,
                    }}>
                      {r.reqNo}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.7rem', color: t.textMuted }}>
                    {moment(r.at).format('HH:mm')} · {r.insured ? 'Insured' : formatWithOutSom(r.amount)}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={r.insured ? 'Worklist' : 'Pending'}
                  sx={chipSx(t, r.insured ? t.accent.emerald : t.accent.amber)}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {existingRequests.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.75, borderRadius: 2,
            bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
              Already on file today
            </Typography>
            <Chip
              size="small"
              icon={<HospitalIcon sx={{ fontSize: 14 }} />}
              label={existingRequests.length}
              sx={chipSx(t, t.accent.blue)}
            />
          </Stack>
          <Stack spacing={0.5}>
            {existingRequests.slice(0, 8).map((r, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '0.76rem', color: t.textMuted,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                · {r.lab}
              </Typography>
            ))}
            {existingRequests.length > 8 && (
              <Typography sx={{ fontSize: '0.72rem', color: t.textDim }}>
                …and {existingRequests.length - 8} more
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
